/**
 * Inbox Dashboard (User V2)
 * ==========================
 * 
 * Two-column layout:
 * - Left: Generic dashboard with inbox list (ID, RFQ Name, Notification, Date)
 * - Right: Chatbox for selected inbox item
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import UserLayout from '../../layout/UserLayout';
import DataDashboard from '../../../common/DataDashboard';
import InboxChatbox from './InboxChatbox';
import AddParticipantModal from './AddParticipantModal';
import SupplierSelectionModal from '../../modals/SupplierSelectionModal';
import RequestSummaryModal from '../../modals/RequestSummaryModal';
import RfqLockedModal from '../../../common/RfqLockedModal';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { useSocket } from '../../../../contexts/SocketContext';
import { checkRfqLock, LockInfo } from '../../../../utils/rfqLock';
import './InboxDashboard.scss';

interface InboxItem {
  id: string;
  rfq_id: string;
  rfq_name: string;
  notification_type: string;
  approval?: string;
  date: string;
  reject_reason?: string;
  unread_count: number;
  request_id?: string;
  allowed_user_ids?: string[];
  participants?: Array<{ user_id: string; name: string; email: string }>;
  is_mentioned?: boolean;
  phase?: string;
  rfq_count?: number;
  nr_of_quotes?: number;
  company_approval_status?: string;
  control_status?: string;
  company_admin_rejection?: boolean;
}

interface ChatMessage {
  message: string;
  sender: 'system' | 'user';
  sender_id?: string;
  sender_name: string;
  timestamp?: string;
  read: boolean;
}

interface InboxDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  phaseFilter?: 'open' | 'finalized'; // Filter by phase: 'open' or 'finalized'
}

const InboxDashboard: React.FC<InboxDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  phaseFilter = 'open' // Default to 'open'
}) => {
  // Log phaseFilter on mount/update to debug
  useEffect(() => {
    console.log('🔍 InboxDashboard mounted/updated with phaseFilter:', phaseFilter, 'route:', currentRoute);
  }, [phaseFilter, currentRoute]);
  const { token, user: authUser } = useAuth();
  const { socket, isConnected, joinInbox, leaveInbox } = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cartItemCount, setCartItemCount] = useState(0);
  const [inboxItems, setInboxItems] = useState<InboxItem[]>([]);
  const [selectedInboxId, setSelectedInboxId] = useState<string | null>(null);
  const selectedInboxIdRef = useRef<string | null>(null); // Ref to preserve selection during polling
  const [selectedInboxItem, setSelectedInboxItem] = useState<InboxItem | null>(null);
  const [selectedItemsForExport, setSelectedItemsForExport] = useState<string[]>([]); // For multiselect/export
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showSupplierSelectionModal, setShowSupplierSelectionModal] = useState(false);
  const [showRequestSummaryModal, setShowRequestSummaryModal] = useState(false);
  const [summaryRequestId, setSummaryRequestId] = useState<string | null>(null);
  const [summaryRfqName, setSummaryRfqName] = useState<string>('');
  const [supplierSelectionRequestId, setSupplierSelectionRequestId] = useState<string | null>(null);
  const [supplierSelectionRfqName, setSupplierSelectionRfqName] = useState<string>('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  // Lock modal state
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockInfo, setLockInfo] = useState<LockInfo | null>(null);
  // Load chat open/closed state from localStorage, default to true (open)
  const [isChatOpen, setIsChatOpen] = useState(() => {
    const saved = localStorage.getItem('inbox_chat_open');
    return saved !== null ? saved === 'true' : true;
  });

  // Check URL for selected inbox ID (from back navigation)
  useEffect(() => {
    const inboxIdParam = searchParams.get('inboxId');
    if (inboxIdParam && inboxIdParam !== selectedInboxId) {
      setSelectedInboxId(inboxIdParam);
      // Remove query parameter after using it
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('inboxId');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, selectedInboxId, setSearchParams]);

  useEffect(() => {
    fetchCartCount();
    // Reset selected inbox when phaseFilter changes
    setSelectedInboxId(null);
    setSelectedInboxItem(null);
    setInboxItems([]);
    setCurrentPage(1); // Reset to page 1 when phase changes
    fetchInboxItems();

    // NOTE: Removed polling - using WebSocket for real-time updates now
  }, [authUser?._id, token, phaseFilter]);

  // Debounced search term - waits 300ms after user stops typing before triggering API call
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState<Record<string, string>>({});

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce column filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnFilters(columnFilters);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnFilters]);

  // Re-fetch when pagination, sorting, or debounced filtering changes
  useEffect(() => {
    fetchInboxItems();
  }, [currentPage, itemsPerPage, sortConfig, debouncedSearchTerm, debouncedColumnFilters]);

  useEffect(() => {
    if (selectedInboxId) {
      fetchChatHistory(selectedInboxId);

      // Join the inbox room for realtime updates
      if (isConnected) {
        joinInbox(selectedInboxId);
      }
    } else {
      setChatHistory([]);
    }

    return () => {
      // Leave room when switching to another inbox
      if (selectedInboxId && isConnected) {
        leaveInbox(selectedInboxId);
      }
    };
  }, [selectedInboxId, token, isConnected]);

  // === WebSocket Event Listeners for Real-time Updates ===
  useEffect(() => {
    if (!socket || !isConnected) return;

    // Handle new message event
    const handleNewMessage = (data: { inbox_id: string; message: any }) => {
      console.log('🔌 Socket: new_message received', data);

      // If we're viewing this inbox, append the message to chat
      if (data.inbox_id === selectedInboxIdRef.current && data.message) {
        setChatHistory(prev => {
          // Avoid duplicates
          const exists = prev.some(m =>
            m.message === data.message.message &&
            m.sender_id === data.message.sender_id &&
            m.timestamp === data.message.timestamp
          );
          if (exists) return prev;

          return [...prev, {
            message: data.message.message,
            sender: data.message.sender,
            sender_id: data.message.sender_id,
            sender_name: data.message.sender_name,
            timestamp: data.message.timestamp,
            mentioned_user_ids: data.message.mentioned_user_ids || [],
            read: false
          }];
        });
      }
    };

    // Handle inbox updated event (refresh list)
    const handleInboxUpdated = () => {
      console.log('🔌 Socket: inbox_updated received');
      fetchInboxItems(false); // Silent refresh

      // Dispatch event to update sidebar unread count
      window.dispatchEvent(new CustomEvent('inbox-updated'));
    };

    socket.on('new_message', handleNewMessage);
    socket.on('inbox_updated', handleInboxUpdated);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('inbox_updated', handleInboxUpdated);
    };
  }, [socket, isConnected]);

  const fetchCartCount = async () => {
    const localToken = localStorage.getItem('auth_token');
    try {
      const response = await fetch(API_ENDPOINTS.PREREQUESTS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localToken}`,
          'X-User-ID': authUser?._id || '',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCartItemCount(data.data?.items?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching cart count:', error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!token) return;

    try {
      // Use new mark-status endpoint
      const response = await fetch(`${API_ENDPOINTS.USER_INBOX}/mark-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mark_all: true,
          phase: phaseFilter,
          status: 'read'
        })
      });

      const data = await response.json();
      if (data.success) {
        // Optimistically update local state
        setInboxItems(prev => prev.map(item => ({
          ...item,
          unread_count: 0,
          is_mentioned: false
        })));

        // Trigger silent update
        fetchInboxItems(false);
        // Dispatch global event
        window.dispatchEvent(new CustomEvent('inbox-updated'));
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      alert('Failed to mark all as read');
    }
  };

  const handleMarkUnread = async () => {
    if (!token) return;

    const hasSelection = selectedItemsForExport.length > 0;

    try {
      const payload = hasSelection
        ? { inbox_ids: selectedItemsForExport, status: 'unread' }
        : { mark_all: true, phase: phaseFilter, status: 'unread' };

      const response = await fetch(`${API_ENDPOINTS.USER_INBOX}/mark-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to mark as unread');

      const data = await response.json();
      if (data.success) {
        fetchInboxItems();
        // Clear selection if we acted on selection
        if (hasSelection) {
          setSelectedItemsForExport([]);
        }
        window.dispatchEvent(new CustomEvent('inbox-updated'));
      }
    } catch (err) {
      console.error('Error marking as unread:', err);
      alert('Failed to mark as unread');
    }
  };


  const fetchInboxItems = async (showLoading = true) => {
    if (!token) return;

    if (showLoading) {
      setIsLoading(true);
      setError(null);
    }

    try {
      // Build query params for backend filtering/pagination/sorting
      const params = new URLSearchParams();
      params.set('page', currentPage.toString());
      params.set('limit', itemsPerPage.toString());
      params.set('phase_filter', phaseFilter);

      // Search (using debounced value)
      if (debouncedSearchTerm.trim()) {
        params.set('search', debouncedSearchTerm.trim());
      }

      // Column filters - add to search (using debounced values)
      const filterValues = Object.values(debouncedColumnFilters).filter(v => v.trim());
      if (filterValues.length > 0) {
        // Combine column filters with search
        const combinedSearch = [debouncedSearchTerm, ...filterValues].filter(v => v.trim()).join(' ');
        if (combinedSearch) {
          params.set('search', combinedSearch);
        }
      }

      // Sorting
      if (sortConfig) {
        params.set('sort_by', sortConfig.key);
        params.set('sort_order', sortConfig.direction);
      }

      const url = `${API_ENDPOINTS.USER_INBOX}?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Backend now returns filtered, sorted, paginated data with all request details
        // No need for additional API calls!
        const items: InboxItem[] = data.data || [];
        const pagination = data.pagination || {};

        console.log('🔍 InboxDashboard - Backend returned:', items.length, 'items');
        console.log('🔍 InboxDashboard - Pagination:', pagination);

        if (data.pagination) {
          setPaginationInfo(data.pagination);
        }

        // Only update state if data actually changed (to prevent unnecessary re-renders)
        setInboxItems(prevItems => {
          const prevIds = prevItems.map(item => `${item.id}:${item.unread_count}`).sort().join(',');
          const newIds = items.map(item => `${item.id}:${item.unread_count}`).sort().join(',');

          if (prevIds !== newIds) {
            return items;
          }
          return prevItems;
        });

        // Handle selection preservation - use ref to get the most current value
        const currentSelectedId = selectedInboxIdRef.current || selectedInboxId;

        setSelectedInboxId(prevSelectedId => {
          const actualSelectedId = selectedInboxIdRef.current || prevSelectedId;

          // Auto-select item from URL parameter if available
          const inboxIdParam = searchParams.get('inboxId');
          if (inboxIdParam && items.find((item: InboxItem) => item.id === inboxIdParam)) {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('inboxId');
            setSearchParams(newSearchParams, { replace: true });
            selectedInboxIdRef.current = inboxIdParam;
            return inboxIdParam;
          }

          if (showLoading) {
            // Initial load: select first item if no selection
            if (items.length > 0 && !actualSelectedId) {
              selectedInboxIdRef.current = items[0].id;
              return items[0].id;
            }
            selectedInboxIdRef.current = actualSelectedId;
            return actualSelectedId;
          } else {
            // Polling/silent update: preserve current selection if it exists
            if (actualSelectedId) {
              const stillExists = items.find((item: InboxItem) => item.id === actualSelectedId);
              if (stillExists) {
                return actualSelectedId;
              }
              if (items.length > 0) {
                selectedInboxIdRef.current = items[0].id;
                return items[0].id;
              }
              return actualSelectedId;
            }
            return actualSelectedId;
          }
        });
      } else {
        throw new Error(data.error || 'Failed to load inbox items');
      }
    } catch (err: any) {
      console.error('Error fetching inbox items:', err);
      if (showLoading) {
        setError(err.message || 'Failed to load inbox items');
      }
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  const fetchChatHistory = async (inboxId: string, silent = false) => {
    if (!token) return;

    if (!silent) {
      setIsLoadingChat(true);
    }
    try {
      const response = await fetch(API_ENDPOINTS.USER_INBOX_ITEM(inboxId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        const newChatHistory = data.data.chat_history || [];

        // Set chat history directly (React will handle diff internally)
        setChatHistory(newChatHistory);

        // Update selected inbox item immediately (don't iterate all items)
        const currentItem = inboxItems.find(item => item.id === inboxId);
        if (currentItem) {
          setSelectedInboxItem({
            ...currentItem,
            unread_count: 0,
            allowed_user_ids: data.data.allowed_user_ids,
            participants: data.data.participants,
            is_mentioned: false
          });
        }

        // Update inbox items in background (debounced via setTimeout to not block UI)
        if (!silent) {
          requestAnimationFrame(() => {
            setInboxItems(prevItems =>
              prevItems.map(item =>
                item.id === inboxId
                  ? { ...item, unread_count: 0, is_mentioned: false }
                  : item
              )
            );
          });
        }

        // Scroll position restoration is handled by useEffect in InboxChatbox component
      }

      // Note: We already update unread_count locally, no need to refetch entire inbox list

      // Dispatch event to update sidebar unread count
      window.dispatchEvent(new CustomEvent('inbox-updated'));
    } catch (err: any) {
      console.error('Error fetching chat history:', err);
    } finally {
      setIsLoadingChat(false);
    }
  };

  const handleSendMessage = async (message: string, mentionedUserIds: string[] = []) => {
    if (!selectedInboxId || !token) return;

    try {
      const response = await fetch(API_ENDPOINTS.USER_INBOX_MESSAGE(selectedInboxId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message,
          mentioned_user_ids: mentionedUserIds
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Refresh chat history (this will also update inbox items silently)
        await fetchChatHistory(selectedInboxId);
        // Dispatch event to update sidebar unread count
        window.dispatchEvent(new CustomEvent('inbox-updated'));
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      throw err;
    }
  };

  const fetchCompanyId = async (requestId: string) => {
    if (!token || !requestId || companyId) return;

    try {
      const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL(requestId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.company_id) {
          setCompanyId(data.data.company_id);
        }
      }
    } catch (err: any) {
      console.error('Error fetching company ID:', err);
    }
  };

  const handleAddParticipant = () => {
    // Fetch company_id if not available
    if (selectedInboxItem?.request_id && !companyId) {
      fetchCompanyId(selectedInboxItem.request_id);
    }
    setShowAddParticipantModal(true);
  };

  const handleAddParticipants = async (userIds: string[]) => {
    if (!selectedInboxId || !token || userIds.length === 0) return;

    try {
      const response = await fetch(API_ENDPOINTS.USER_INBOX_ADD_PARTICIPANTS(selectedInboxId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_ids: userIds })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Refresh chat history to get updated participants
        await fetchChatHistory(selectedInboxId);
        // Refresh inbox items
        await fetchInboxItems(false);
        setShowAddParticipantModal(false);
        // Dispatch event to update sidebar unread count
        window.dispatchEvent(new CustomEvent('inbox-updated'));
      }
    } catch (err: any) {
      console.error('Error adding participants:', err);
      alert(`Error adding participants: ${err.message}`);
    }
  };

  const handleRowClick = (item: any) => {
    // Save scroll position before switching
    if (selectedInboxId && selectedInboxId !== item.id) {
      const chatContainer = document.querySelector('.inbox-chatbox__messages');
      if (chatContainer) {
        localStorage.setItem(`inbox_scroll_${selectedInboxId}`, String(chatContainer.scrollTop));
      }
    }
    // Update ref when selection changes
    selectedInboxIdRef.current = item.id;

    setSelectedInboxId(item.id);
    setSelectedInboxItem(item);

    // Clear old chat immediately and show loading (perceived performance boost)
    // Note: fetchChatHistory is called automatically by useEffect when selectedInboxId changes
    setChatHistory([]);
    setIsLoadingChat(true);
  };

  const handleOpenRequest = async (itemId: string) => {
    // Save current scroll position before navigating
    const chatContainer = document.querySelector('.inbox-chatbox__messages');
    if (chatContainer && selectedInboxId === itemId) {
      localStorage.setItem(`inbox_scroll_${itemId}`, String(chatContainer.scrollTop));
    }

    const item = inboxItems.find(i => i.id === itemId);
    if (!item?.request_id) return;

    // Check if RFQ is locked by another user (skip for admins)
    if (token) {
      const lockCheck = await checkRfqLock(item.request_id, token);
      if (lockCheck.locked && !lockCheck.is_admin && lockCheck.lock_info) {
        // RFQ is locked by another user from same company
        setLockInfo(lockCheck.lock_info);
        setShowLockModal(true);
        return;
      }
    }

    // Check Process RFQ setting - if Auto AI, use old flow (no secondary sidebar)
    // Backend stores process_rfq as boolean: true = "auto ai", false = "manual"
    const processRfq = (user as any)?.process_rfq ?? (user as any)?.permissions?.process_rfq;
    if (processRfq === true || processRfq === 'auto ai' || processRfq === 'Auto AI' || processRfq === 'auto_ai') {
      // Auto AI: navigate to open RFQ detail page (old flow, no secondary sidebar)
      onNavigate(`/dashboard/rfqs/open/${item.request_id}?from=inbox&inboxId=${itemId}`);
      return;
    }

    // Manual Process RFQ: navigate to user RFQ route with secondary sidebar
    // If phase is select_supplier, navigate directly to quotes review page
    if (item.phase === 'select_supplier') {
      setSupplierSelectionRequestId(item.request_id);
      setSupplierSelectionRfqName(item.rfq_name || 'RFQs');
      setShowSupplierSelectionModal(true);
      return;
    }

    // If phase is completed, show summary modal
    if (item.phase === 'completed') {
      setSummaryRequestId(item.request_id);
      setSummaryRfqName(item.rfq_name || 'RFQs');
      setShowRequestSummaryModal(true);
      return;
    }

    onNavigate(`/dashboard/user/rfq/${item.request_id}?from=inbox&inboxId=${itemId}`);
  };

  // We rely on backend for filtering/sorting/pagination
  // Client-side logic removed to prevent double-processing bugs


  // Wrapper for DataDashboard's onDataFetch prop
  const handleDataFetch = (params: {
    page: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    columnFilters?: Record<string, string>;
  }) => {
    // Update pagination state
    if (params.page) {
      setCurrentPage(params.page);
    }

    // Update items per page
    if (params.limit && params.limit !== itemsPerPage) {
      setItemsPerPage(params.limit);
      setCurrentPage(1); // Reset to first page ONLY when changing limit
    }

    // Update search term
    if (params.search !== undefined && params.search !== searchTerm) {
      setSearchTerm(params.search);
      setDebouncedSearchTerm(params.search);
      setCurrentPage(1); // Reset to first page ONLY when searching
    }

    // Update sort config
    if (params.sortBy) {
      const newSort = {
        key: params.sortBy,
        direction: (params.sortOrder as 'asc' | 'desc') || 'asc'
      };
      // Only update if changed
      if (!sortConfig || sortConfig.key !== newSort.key || sortConfig.direction !== newSort.direction) {
        setSortConfig(newSort);
      }
    }

    // Update column filters
    if (params.columnFilters !== undefined) {
      // Check if filters actually changed using JSON stringify for deep comparison
      const filtersChanged = JSON.stringify(params.columnFilters) !== JSON.stringify(columnFilters);

      if (filtersChanged) {
        setColumnFilters(params.columnFilters);
        setDebouncedColumnFilters(params.columnFilters);
        setCurrentPage(1); // Reset to first page ONLY when filtering changed
      }
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const formatNotificationType = (type: string) => {
    const typeMap: Record<string, string> = {
      'rejected': 'Rejected',
      'approved': 'Approved',
      'pending': 'Pending',
      'completed': 'Completed',
      'new request': 'New Request',
      'User question': 'User Question',
      'Clarification needed': 'Clarification Needed',
      'RFQ Closed': 'RFQ Closed',
      'RFQ Modified': 'RFQ Modified'
    };
    return typeMap[type] || type;
  };

  const formatApproval = (value: string) => {
    const approvalMap: Record<string, string> = {
      'approval expected': 'Approval Expected',
      'approved': 'Approved',
      'rejected': 'Rejected'
    };
    return approvalMap[value] || value;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Format phase name (same as OpenRFQDashboard)
  const formatPhaseName = (phase?: string) => {
    if (!phase) return '-';
    const phaseMap: Record<string, string> = {
      'document_preparation': 'Document Preparation',
      'define_supplier_list': 'Define Supplier List',
      'send_out_rfq': 'Send Out RFQ',
      'receive_responses': 'Receive Responses',
      'select_supplier': 'Select the Supplier',
      'advise_unsuccessful_suppliers': 'Advise Unsuccessful Suppliers',
      'completed': 'Completed',
      'rejected': 'Rejected',
      'cancelled': 'Cancelled',
      'deleted': 'Deleted'
    };
    return phaseMap[phase] || phase;
  };

  // Calculate phase progress (1-5 scale)
  const getPhaseProgress = (phase?: string) => {
    if (!phase) return 1;
    const progressMap: Record<string, number> = {
      'document_preparation': 1,
      'define_supplier_list': 2.5,
      'send_out_rfq': 3,
      'receive_responses': 4,
      'select_supplier': 5,
      'advise_unsuccessful_suppliers': 5,
      'completed': 5
    };
    return progressMap[phase] || 1;
  };

  // Custom cell renderer for phase with progress bar (same as OpenRFQDashboard)
  const renderPhaseCell = (value: any, row: any) => {
    let phaseName = formatPhaseName(value);
    let progress = getPhaseProgress(value);
    const isDeleted = value === 'deleted';

    // Override display for Document Preparation phase to show sub-statuses
    if (value === 'document_preparation') {
      if (row.company_admin_rejection) {
        phaseName = 'Approval Rejected';
        // Keep progress as 1
      } else if (row.control_status === 'rejected') {
        phaseName = 'Control Rejected';
      } else if (row.company_admin_approval === true) {
        // Approved - check control status
        if (row.control_status === 'waiting') {
          phaseName = 'Control Request';
          progress = 1.5; // Slightly more progress
        } else if (row.control_status === 'confirmed' || row.control_status === 'skipped') {
          phaseName = 'Document Preparation';
          progress = 2; // Ready for next phase
        } else {
          phaseName = 'Document Preparation';
          progress = 2;
        }
      } else if (row.company_admin_approval === false) {
        // Not approved yet - show approve request
        phaseName = 'Approve Request';
      }
    }

    const percentage = (progress / 5) * 100;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={{ color: isDeleted ? '#dc2626' : 'inherit', fontWeight: isDeleted ? '600' : 'normal' }}>
          {phaseName}
        </span>
        {!isDeleted && value && (
          <div style={{
            width: '100%',
            height: '6px',
            backgroundColor: '#e0e0e0',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${percentage}%`,
              height: '100%',
              backgroundColor: '#808080',
              transition: 'width 0.3s ease'
            }} />
          </div>
        )}
      </div>
    );
  };

  // Define columns for DataDashboard
  const inboxColumns = [
    {
      key: 'rfq_id',
      title: 'ID',
      type: 'text' as const,
      sortable: true,
      filterable: true,
      width: '150px'
    },
    {
      key: 'rfq_name',
      title: 'RFQ Name',
      type: 'text' as const,
      sortable: true,
      filterable: true,
      width: '200px'
    },
    {
      key: 'owner_name',
      title: 'Owner',
      type: 'text' as const,
      sortable: true,
      filterable: true,
      width: '120px'
    },
    {
      key: 'phase',
      title: 'Phase',
      type: 'text' as const,
      sortable: true,
      filterable: true,
      width: '200px',
      render: renderPhaseCell
    },
    {
      key: 'approved_by_name',
      title: 'Approved By',
      type: 'text' as const,
      sortable: true,
      filterable: true,
      width: '150px',
      defaultHidden: true
    },
    {
      key: 'controlled_by_name',
      title: 'Controlled By',
      type: 'text' as const,
      sortable: true,
      filterable: true,
      width: '150px',
      defaultHidden: true
    },
    {
      key: 'rfq_count',
      title: 'RFQ Count',
      type: 'number' as const,
      sortable: true,
      filterable: true,
      width: '120px',
      render: (value: number) => value ?? '-'
    },
    {
      key: 'nr_of_quotes',
      title: 'Number of Quotes',
      type: 'number' as const,
      sortable: true,
      filterable: true,
      width: '150px',
      render: (value: number) => value ?? '-'
    },
    {
      key: 'notification_type',
      title: 'Notification',
      type: 'text' as const,
      sortable: true,
      filterable: true,
      width: '150px',
      render: (value: string) => formatNotificationType(value)
    },
    {
      key: 'approval',
      title: 'Approval',
      type: 'text' as const,
      sortable: true,
      filterable: true,
      width: '150px',
      render: (value: string) => formatApproval(value || 'approval expected')
    },
    {
      key: 'date',
      title: 'Date',
      type: 'date' as const,
      sortable: true,
      filterable: true,
      width: '180px',
      render: (value: string) => formatDate(value)
    }
  ];

  const pageTitle = phaseFilter === 'finalized' ? 'Finalized RFQ' : 'Open RFQ';
  const pageDescription = phaseFilter === 'finalized'
    ? 'My Finalized RFQs and internal communication'
    : 'My open RFQs and internal communication';

  return (
    <UserLayout
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      pageTitle={pageTitle}
      pageDescription={pageDescription}
      cartItemCount={cartItemCount}
      noSidebarMargin={true}
      hideHeader={true}
      noContentPadding={true}
    >
      <div className="inbox-dashboard inbox-dashboard-page" style={{ paddingBottom: '32px' }}>
        <div className={`inbox-dashboard__container ${isChatOpen ? 'inbox-dashboard__container--chat-open' : 'inbox-dashboard__container--chat-closed'}`}>
          {/* Left side - DataDashboard */}
          <div className="inbox-dashboard__left">
            <DataDashboard
              title={pageTitle}
              pageDescription={pageDescription}
              collection="user_inbox"
              data={inboxItems}
              columns={inboxColumns}
              isLoading={isLoading}
              error={error}
              user={user}
              currentRoute={currentRoute}
              onNavigate={onNavigate}
              onLogout={onLogout}
              onDataChange={setInboxItems}
              onDataFetch={handleDataFetch}
              paginationInfo={paginationInfo}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              showAddButton={false}
              showSearch={true}
              showSelection={true}
              showEditButton={false}
              showDeleteButton={false}
              layoutType="user"
              onRowClick={handleRowClick}
              onOpen={handleOpenRequest}
              selectedItems={selectedItemsForExport}
              onSelectionChange={(ids) => {
                // Update multiselect state for export - independent of chat selection
                setSelectedItemsForExport(ids);
              }}
              getRowClassName={(row) => {
                const classes = [];
                // Only show mention color if there are unread messages (mention is unread)
                if (row.is_mentioned && (row.unread_count || 0) > 0) {
                  classes.push('data-table-row--mentioned');
                } else if ((row.unread_count || 0) > 0) {
                  classes.push('data-table-row--unread');
                }
                if (row.id === selectedInboxId) {
                  classes.push('data-table-row--selected');
                }
                return classes.join(' ');
              }}
              headerActions={[
                {
                  key: 'mark-unread',
                  label: selectedItemsForExport.length > 0 ? `Mark Unread (${selectedItemsForExport.length})` : 'Mark All Unread',
                  onClick: handleMarkUnread,
                  className: 'btn-secondary',
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 6.5H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M4 10C4 10 4 19 4 21C4 21.55 4.5 22 5 22H19C19.55 22 20 21.55 20 21C20 19 20 10 20 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M16 22V14C16 12 14.5 9 12 9C9.5 9 8 12 8 14V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  )
                },
                {
                  key: 'mark-all-read',
                  label: 'Mark All as Read',
                  onClick: handleMarkAllRead,
                  className: 'btn-secondary',
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )
                }
              ]}
            />
          </div>

          {/* Right side - Chatbox */}
          <div className={`inbox-dashboard__right ${isChatOpen ? 'inbox-dashboard__right--open' : 'inbox-dashboard__right--closed'}`}>
            <InboxChatbox
              inboxId={selectedInboxId}
              chatHistory={chatHistory}
              onSendMessage={handleSendMessage}
              isLoading={isLoadingChat}
              currentUserId={authUser?._id}
              requestId={selectedInboxItem?.request_id}
              rfqId={selectedInboxItem?.rfq_id}
              participants={selectedInboxItem?.participants}
              onAddParticipant={handleAddParticipant}
            />
          </div>

          {/* Toggle button for chat - outside panels, positioned relative to container */}
          <div className="inbox-dashboard__chat-toggle-wrapper">
            <button
              className={`inbox-dashboard__chat-toggle ${isChatOpen ? 'inbox-dashboard__chat-toggle--open' : 'inbox-dashboard__chat-toggle--closed'}`}
              onClick={() => {
                const newState = !isChatOpen;
                setIsChatOpen(newState);
                localStorage.setItem('inbox_chat_open', String(newState));
              }}
              title={isChatOpen ? 'Close chat' : 'Open chat'}
            >
              {isChatOpen ? '▶' : '◀'}
            </button>
            {!isChatOpen && (
              <div className="inbox-dashboard__chat-label">
                <span>c</span>
                <span>h</span>
                <span>a</span>
                <span>t</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Participant Modal */}
      {(companyId || authUser?.company_id) && (
        <AddParticipantModal
          isOpen={showAddParticipantModal}
          onClose={() => setShowAddParticipantModal(false)}
          onAdd={handleAddParticipants}
          companyId={companyId || authUser?.company_id || ''}
          existingParticipantIds={(() => {
            const allowedIds = selectedInboxItem?.allowed_user_ids || [];
            // Also collect participants from chat history
            const chatParticipantIds = new Set<string>();
            chatHistory.forEach(msg => {
              if (msg.sender_id) chatParticipantIds.add(String(msg.sender_id));
            });
            // Combine both
            return Array.from(new Set([...allowedIds, ...Array.from(chatParticipantIds)]));
          })()}
        />
      )}

      {supplierSelectionRequestId && (
        <SupplierSelectionModal
          isOpen={showSupplierSelectionModal}
          onClose={() => setShowSupplierSelectionModal(false)}
          requestId={supplierSelectionRequestId}
          rfqName={supplierSelectionRfqName}
          onSuccess={() => {
            // Refresh inbox to show updated status or counts if improved later
            fetchInboxItems(false);
          }}
        />
      )}

      {summaryRequestId && (
        <RequestSummaryModal
          isOpen={showRequestSummaryModal}
          onClose={() => setShowRequestSummaryModal(false)}
          requestId={summaryRequestId}
          rfqName={summaryRfqName}
        />
      )}

      {/* RFQ Locked Modal */}
      <RfqLockedModal
        isOpen={showLockModal}
        lockInfo={lockInfo}
        onClose={() => setShowLockModal(false)}
        onGoBack={() => setShowLockModal(false)}
      />
    </UserLayout>
  );
};

export default InboxDashboard;
